import type { PermissionSlug } from '../services/userPermissionsService';
import type { UserClaims } from './useRBAC';

/**
 * WHAT A PERSON MAY OPEN — one table, read by the menu and by the router (INV-108).
 *
 * Until this existed the web asked nothing at the page level: every main screen mounted for
 * everybody, its list read 403'd, and the page rendered its EMPTY state — "No patients yet" with
 * an "Add the first patient" button, a form that fills in and refuses on submit. The product
 * owner's ask (2026-09-21) is the opposite shape: say so up front, and offer nothing.
 *
 * ⚠ THE SLUGS ARE THE SERVER'S, NOT A GUESS. Each row carries the conjunction that screen's LIST
 * read really requires — `GET /clients` → `view:clients`, `GET /horses` → `view:patients`, and so
 * on — and `backend/src/auth/screen-access-binding.spec.ts` re-derives them from the controller
 * decorators and fails when the two disagree. A UI gate is never the enforcement point (the
 * backend guard is); what this table buys is that the UI refuses for the SAME reason the API
 * does, so a grant opens the screen and a revoke closes it with no second vocabulary in between.
 *
 * ⚠ REPORTS IS DELIBERATELY ABSENT. It keeps `auth/canOpenReports.ts`, whose OWNER arm is sound
 * only because `view:reports` is owner-locked server-side (INV-104); no other `view:` slug
 * carries that guarantee, so spreading the arm across ten screens would be a UI affordance over
 * an API that refuses. Dashboard and Settings are always reachable — the Dashboard's own tiles
 * and the Settings tabs gate themselves.
 */
export interface ScreenAccessEntry {
  /** Stable key — the router and the menu name the screen by this, never by its path. */
  readonly key: ScreenKey;
  /** Every route in `App.tsx` this screen owns. The FIRST is where its nav item points. */
  readonly paths: readonly string[];
  /** The `menuItems` label in `App.tsx`, or null when the screen has no nav item. */
  readonly navText: string | null;
  /** ALL required — the server's conjunction for this screen's list read. */
  readonly view: readonly PermissionSlug[];
  /**
   * ALL required — the server's conjunction for this screen's PRIMARY create route, and so for
   * its ordinary create/edit/delete controls: the toolbar Add, the empty-state call to action,
   * the mobile FAB, the row Edit/Delete/Archive.
   *
   * A control whose own handler wants a DIFFERENT slug does NOT use this — it uses one of the
   * named constants below, each citing the controller line it copies. Recording payments on the
   * Invoicing screen is the shape to keep in mind: `manage:charges` opens the invoice form and
   * `manage:payments` opens the payment dialog, and a person can hold either without the other.
   */
  readonly manage: readonly PermissionSlug[];
  /** Reads into both sentences: "…access to view ${noun}". Lower case, article included. */
  readonly noun: string;
  /**
   * SHOW THIS NAV ITEM WHILE THE ANSWER IS STILL PENDING?
   *
   * `true` for every screen in this table, and the reason is a majority argument rather than an
   * absolute: almost everybody holds almost all of these, so hiding them for the length of a cold
   * load would empty the sidebar and re-fill it a beat later — which reads as a broken app, where
   * the rare wrongly-shown item reads as a link to a sentence.
   *
   * `false` is for the screen most people DON'T have, where the same arithmetic runs the other
   * way: a link that appears and then vanishes on every cold load looks like something being
   * taken away. Reports is that screen — it is not a row here (it keeps `auth/canOpenReports.ts`,
   * whose OWNER arm no other `view:` slug can justify), so its answer lives beside its rule, as
   * `REPORTS_SHOW_WHILE_PENDING`.
   *
   * ⚠ It only covers PENDING. A read that FAILED still shows everything, for both kinds of
   * screen: that state persists rather than flashing, the shell puts a banner above it, and the
   * route says "we couldn't check" in place if the item is clicked.
   */
  readonly showWhilePending: boolean;
}

export type ScreenKey =
  | 'schedule'
  | 'patients'
  | 'clients'
  | 'barns'
  | 'barnVisits'
  | 'barnOperations'
  | 'refillRequests'
  | 'inventory'
  | 'invoicing'
  | 'treatmentSheet'
  | 'resultsInbox';

const entry = (e: ScreenAccessEntry): ScreenAccessEntry => e;

export const SCREEN_ACCESS: Record<ScreenKey, ScreenAccessEntry> = {
  // appointments.controller.ts `findAll` → view:appointments; `create` → manage:appointments
  schedule: entry({
    key: 'schedule',
    paths: ['/appointments'],
    navText: 'Schedule',
    view: ['view:appointments'],
    manage: ['manage:appointments'],
    noun: 'the schedule',
    showWhilePending: true,
  }),
  // horses.controller.ts `findAll` → view:patients (the chart reads more, per panel);
  // `create` → manage:patients
  patients: entry({
    key: 'patients',
    paths: ['/patients', '/patients/:horseId'],
    navText: 'Patients',
    view: ['view:patients'],
    manage: ['manage:patients'],
    noun: 'patients',
    showWhilePending: true,
  }),
  // clients.controller.ts `findAll` → view:clients; `create` → manage:clients
  clients: entry({
    key: 'clients',
    paths: ['/clients'],
    navText: 'Clients',
    view: ['view:clients'],
    manage: ['manage:clients'],
    noun: 'clients',
    showWhilePending: true,
  }),
  // barns.controller.ts `list` → view:patients, `create` → manage:patients. A barn is a patient
  // grouping on the server, so BOTH slugs are the patients ones — do not invent a `view:barns` or
  // a `manage:barns`; there is no such gate.
  barns: entry({
    key: 'barns',
    paths: ['/barns', '/barns/:id'],
    navText: 'Barns',
    view: ['view:patients'],
    manage: ['manage:patients'],
    noun: 'barns',
    showWhilePending: true,
  }),
  // The visit workspace reads BOTH the day's appointments and the barn, so it needs both slugs:
  // with only one of them half the page is empty and the other half 403s. Its own writes are a
  // clinical note (medical-records.controller.ts `create` → manage:medical_records) and the
  // invoice submit, which is the barn-operations charge route and carries `BARN_CHARGE_PERMISSION`.
  barnVisits: entry({
    key: 'barnVisits',
    paths: ['/barn-visits/:visitGroupId'],
    navText: null,
    view: ['view:appointments', 'view:patients'],
    manage: ['manage:medical_records'],
    noun: 'barn visits',
    showWhilePending: true,
  }),
  // The screen READS barns (view:patients) and WRITES invoices: billing.controller.ts
  // `createBarnOperations` → manage:charges. The two are unrelated slugs on one page, which is
  // exactly why `manage` is its own column rather than being derived from `view`.
  barnOperations: entry({
    key: 'barnOperations',
    paths: ['/barn-operations'],
    navText: null,
    view: ['view:patients'],
    manage: ['manage:charges'],
    noun: 'barn operations',
    showWhilePending: true,
  }),
  // refill-requests.controller.ts `findAll` → view:medical_records; `create` (the log route) →
  // manage:medical_records, and so do verify/approve/deny/request-recheck.
  refillRequests: entry({
    key: 'refillRequests',
    paths: ['/refill-requests'],
    navText: 'Refill Requests',
    view: ['view:medical_records'],
    manage: ['manage:medical_records'],
    noun: 'refill requests',
    showWhilePending: true,
  }),
  // inventory.controller.ts `findAll` → view:inventory; `create` → manage:inventory. Pricing and
  // the DEA tab are NOT this slug — see INVENTORY_PRICING_PERMISSION / DEA_LOG_PERMISSION.
  inventory: entry({
    key: 'inventory',
    paths: ['/inventory'],
    navText: 'Inventory',
    view: ['view:inventory'],
    manage: ['manage:inventory'],
    noun: 'inventory',
    showWhilePending: true,
  }),
  // billing.controller.ts `findAll` → view:billing (the money READ); `create` → manage:charges.
  // Taking money and giving it back are separate slugs: RECORD_PAYMENT_PERMISSION, REFUND_PERMISSION.
  invoicing: entry({
    key: 'invoicing',
    paths: ['/billing', '/billing/bulk-paid'],
    navText: 'Invoicing',
    view: ['view:billing'],
    manage: ['manage:charges'],
    noun: 'invoices',
    showWhilePending: true,
  }),
  // treatment-sheet.controller.ts `findAll` (the stall-board occupancy read) → view:medical_records;
  // `create` (the ADMIT route) → manage:medical_records. Orders, vitals, administrations, clinical
  // notes and the stall CRUD on the sibling controllers all carry the same one slug.
  treatmentSheet: entry({
    key: 'treatmentSheet',
    paths: ['/hospital', '/hospital/treatment-sheets/:id'],
    navText: 'Treatment Sheet',
    view: ['view:medical_records'],
    manage: ['manage:medical_records'],
    noun: 'the treatment sheet',
    showWhilePending: true,
  }),
  // unallocated-results.controller.ts `list` → view:medical_records; `assign` (and reject, and
  // reopen) → manage:medical_records.
  resultsInbox: entry({
    key: 'resultsInbox',
    paths: ['/clinical-records/unallocated'],
    navText: null,
    view: ['view:medical_records'],
    manage: ['manage:medical_records'],
    noun: 'lab and imaging results',
    showWhilePending: true,
  }),
};

/**
 * THE PATIENT CHART'S PANELS — the same table, one level down (INV-108 (b), 2026-09-21).
 *
 * `SCREEN_ACCESS.patients` opens `/patients/:horseId` on `view:patients`, which is the gate on
 * `GET /horses/:id` — the HORSE. It is not the gate on anything the chart then shows. Every
 * clinical section on that page is a read of a DIFFERENT controller, and the product owner found
 * what that gap looks like on staging: with "View medical records" off, the chart still mounted,
 * every section's read 403'd, and each one rendered its own empty state — "No medical records
 * yet" to a person who has no access to them, which is not an absence of records, it is a
 * different sentence entirely. The chart was treated as "already gated" and left out of A1's
 * sweep precisely BECAUSE its route was gated; the route and the panels are two questions.
 *
 * ⚠ AND THE ANSWER IS NOT "ADD view:medical_records TO THE PATIENTS ROW". `screen-access-binding`
 * already refuses that (its anti-widening case), and it would be wrong in the expensive
 * direction: a person who may see patients would lose the whole chart — name, age, owners,
 * alerts, invoices — over one section. Panels gate themselves, in place, and the ones the person
 * CAN read keep working. That boundary is stated in the weld spec and this table is its other
 * half.
 *
 * ⚠ EVERY CLINICAL PANEL LANDS ON ONE SLUG, AND THAT IS THE SERVER'S DOING, NOT A SHORTCUT.
 * `view:medical_records` is what `GET /medical-records`, `/soap-notes`, `/medications`,
 * `/vaccinations`, `/procedures`, `/lab-results`, `/problem-list`, `/recall-reminders`,
 * `/discharge-summaries`, `/attachments`, `/client-communications`, the horse's treatment sheets
 * AND the Keystone/IDEXX result reads all declare — twelve controllers, one gate, each cited on
 * its row below. They are kept as SEPARATE ROWS rather than collapsed into one flag so that a
 * re-pointed gate moves one panel instead of silently moving twelve, and so the weld can name
 * the panel that drifted. Invoices is the one row that differs, and it differs today.
 */
export type ChartPanelKey =
  | 'medicalRecords'
  | 'labs'
  | 'medications'
  | 'vaccinations'
  | 'procedures'
  | 'dischargeSummaries'
  | 'treatmentSheets'
  | 'problems'
  | 'recalls'
  | 'files'
  | 'communications'
  | 'invoices';

export interface ChartPanelEntry {
  /** Stable key — the chart names a panel by this, never by its tab index or its chip label. */
  readonly key: ChartPanelKey;
  /** ALL required — the server's conjunction for THIS panel's list read. */
  readonly view: readonly PermissionSlug[];
  /** Reads into the sentence: "…access to view ${noun}". Lower case, what the tab is called. */
  readonly noun: string;
}

const panel = (e: ChartPanelEntry): ChartPanelEntry => e;

export const CHART_PANELS: Record<ChartPanelKey, ChartPanelEntry> = {
  // medical-records.controller.ts `findAll` → view:medical_records. On the Modern layout this is
  // the TIMELINE, which merges records, SOAP notes, meds, vaccines and procedures into one feed;
  // on Classic it is the All / SOAP Notes / Visit Notes tabs. One row for all of them, because
  // one refused read empties the lot.
  medicalRecords: panel({
    key: 'medicalRecords',
    view: ['view:medical_records'],
    noun: 'medical records',
  }),
  // lab-results.controller.ts `findAll` → view:medical_records, and so do the three other feeds
  // this panel merges: keystone.controller.ts `getStudiesForHorse` / `listImagingRequests` and
  // idexx.controller.ts `listLabRequests`. The union is one slug.
  labs: panel({ key: 'labs', view: ['view:medical_records'], noun: 'lab and imaging results' }),
  // medications.controller.ts `findAll` → view:medical_records.
  medications: panel({ key: 'medications', view: ['view:medical_records'], noun: 'medications' }),
  // vaccinations.controller.ts `findAll` → view:medical_records.
  vaccinations: panel({ key: 'vaccinations', view: ['view:medical_records'], noun: 'vaccinations' }),
  // procedures.controller.ts `findAll` → view:medical_records.
  procedures: panel({ key: 'procedures', view: ['view:medical_records'], noun: 'procedures' }),
  // discharge-summaries.controller.ts `findAll` → view:medical_records. ⚠ NOT
  // `view:discharge_summaries`: that slug is real and IS declared — by exactly one handler,
  // `emailDischargeSummary` — so gating the LIST on it would hide a panel the API serves.
  dischargeSummaries: panel({
    key: 'dischargeSummaries',
    view: ['view:medical_records'],
    noun: 'discharge summaries',
  }),
  // treatment-sheet.controller.ts `findByHorse` (GET /horses/:horseId/treatment-sheets) →
  // view:medical_records.
  treatmentSheets: panel({
    key: 'treatmentSheets',
    view: ['view:medical_records'],
    noun: 'treatment sheets',
  }),
  // problem-list.controller.ts `findAll` → view:medical_records.
  problems: panel({ key: 'problems', view: ['view:medical_records'], noun: 'the problem list' }),
  // recall-reminders.controller.ts `findAll` → view:medical_records.
  recalls: panel({ key: 'recalls', view: ['view:medical_records'], noun: 'recall reminders' }),
  // attachments.controller.ts `findAll` → view:medical_records. A patient file is a clinical
  // record with a blob on it, and the server files it under the same gate.
  files: panel({ key: 'files', view: ['view:medical_records'], noun: 'files' }),
  // client-communications.controller.ts `findAll` → view:medical_records.
  communications: panel({
    key: 'communications',
    view: ['view:medical_records'],
    noun: 'client communications',
  }),
  // billing.controller.ts `findAll` → view:billing. THE ONE PANEL ON A DIFFERENT SLUG, and the
  // reason this is a table rather than a boolean: a technician holds the clinical slug and not
  // this one, a front desk holds this one and not the clinical slug, and the chart has to be
  // right for both. This panel already said so in its own words since P6; it now says it in the
  // same words as everywhere else.
  invoices: panel({ key: 'invoices', view: ['view:billing'], noun: 'invoices' }),
};

/** Iteration order for every consumer and every test. */
export const CHART_PANEL_KEYS = Object.keys(CHART_PANELS) as ChartPanelKey[];

/**
 * CONTROLS WHOSE HANDLER WANTS A DIFFERENT SLUG FROM THEIR SCREEN'S `manage`.
 *
 * Each is the gate on the ONE route that control calls, copied from the controller named beside
 * it. They are constants rather than inline strings so that the whole off-table set is auditable
 * in one place and so the page that renders the control cannot quietly drift onto the screen's
 * slug — which is the mistake that produces a button somebody's first click 403s on, or (the
 * quieter direction) a button an owner has granted the slug for that never appears.
 *
 * ⚠ AND THE SET IS EXACTLY WHAT THE SERVER ASKS, NOT WHAT THE VOCABULARY OFFERS. Two slugs read
 * like they belong here and do not: `manage:inventory_adjustments` and `manage:controlled_substances`
 * are real, grantable slugs that NO controller requires today (grep the backend — they appear only
 * in `auth/permissions.ts` and the editor catalog). Gating the receive-stock or DEA controls on
 * them would hide a button from somebody the API would serve. The DEA tab's writes are
 * `manage:medical_records` because that is what `dea-logs.controller.ts` declares; the receive
 * dialog is `manage:inventory` because that is what `POST /inventory/:id/receive` declares.
 */

/** clients.controller.ts `linkHorseWithRole` / `unlinkHorseRole` → the PATIENT slug, not the client one. */
export const LINK_HORSE_PERMISSION: PermissionSlug = 'manage:patients';

/** credits.controller.ts `issueCredit` (`POST /credits`) — the one route in the app that asks this. */
export const ISSUE_CREDIT_PERMISSION: PermissionSlug = 'manage:discounts';

/**
 * payments.controller.ts `recordManualPayment` / `recordBatchPayment` / `createPaymentIntent`, and the payment-link routes on
 * billing.controller.ts. NOT `manage:charges`: a front desk that may take a cheque need not be able
 * to write the invoice.
 */
export const RECORD_PAYMENT_PERMISSION: PermissionSlug = 'manage:payments';

/** payments.controller.ts `refundPayment` / `voidPayment` / `deletePayment`, and billing.controller.ts `voidInvoice`. */
export const REFUND_PERMISSION: PermissionSlug = 'manage:refunds';

/** Settings > Team members: users.controller.ts `GET /users` and every user write, `clearApprovalPin` included. */
export const TEAM_MEMBERS_PERMISSION: PermissionSlug = 'manage:users';

/** inventory.controller.ts `setPricing` (`PUT /inventory/:id/pricing`) — its own slug. */
export const INVENTORY_PRICING_PERMISSION: PermissionSlug = 'manage:inventory_pricing';

/**
 * Recording a dose from an expired vaccine lot. A payload gate, not a route: vaccination-lot-patch.ts
 * `assertExpiredLotPermitted`, on POST/PUT /vaccinations, POST /billing/barn-operations and /sync (ADR-0047 A5).
 */
export const EXPIRED_LOT_PERMISSION: PermissionSlug = 'manage:expired_lot_doses';

/**
 * dea-logs.controller.ts — every write on that controller (`createSubstance`, `deactivateSubstance`,
 * `createLot`, `attest`) declares `manage:medical_records`, NOT `manage:controlled_substances`.
 */
export const DEA_LOG_PERMISSION: PermissionSlug = 'manage:medical_records';

/** billing.controller.ts `createBarnOperations` — the barn-visit invoice submit. */
export const BARN_CHARGE_PERMISSION: PermissionSlug = 'manage:charges';

/** medical-records.controller.ts `create`; soap-notes.controller.ts `create` — the clinical writes. */
export const CLINICAL_RECORD_PERMISSION: PermissionSlug = 'manage:medical_records';

/**
 * THE CLINICAL DELETE, which is its own slug and is NOT the one that authored the record.
 *
 * `attachments.controller.ts remove` (`DELETE /attachments/:id`), `medical-records.controller.ts`'s
 * archive/restore/delete, `soap-notes.controller.ts delete`, and the archive arms on medications,
 * vaccinations, procedures and discharge summaries all declare this and not
 * `manage:medical_records`. A clinic can let somebody write records without letting them remove
 * one, which is the whole point of a separate slug on a PHI record, so the chart's remove controls
 * ask for this one.
 *
 * ⚠ THE MED/VAX/PROCEDURE DELETE WANTS BOTH SLUGS, not this one alone —
 * `@RequirePermissions('manage:medical_records', 'manage:medical_records_archive')` on
 * `medications.controller.ts:101`, `vaccinations.controller.ts:98` and `procedures.controller.ts:87`,
 * and `@RequirePermissions` is an AND (`role.guard.ts:48`). That asymmetry is real and is copied
 * exactly in `patientChartControls.removeClinicalRecord`; the attachment delete, which asks for
 * this slug ALONE, is `removeFile`. Two controls rather than one, because merging them would make
 * one of the two wrong.
 */
export const RECORD_ARCHIVE_PERMISSION: PermissionSlug = 'manage:medical_records_archive';

/** Iteration order for every consumer and every test. */
export const SCREEN_KEYS = Object.keys(SCREEN_ACCESS) as ScreenKey[];

/**
 * HIDE A NAV ITEM THE PERSON CANNOT OPEN — one word, both behaviours.
 *
 * The product owner asked for the message on the page; hiding the item is the half he has not
 * confirmed. Flipping this to `false` leaves every item visible and leaves the ROUTE's sentence
 * exactly as it is, so the change is one word and both behaviours are tested (`screenAccess.test.ts`).
 */
export const HIDE_MENU_ITEMS_WITHOUT_ACCESS = true;

/** ALL of the screen's slugs, never any — the server's conjunction, asked exactly. */
export const canViewScreen = (
  screen: ScreenAccessEntry,
  hasPermission: (permission: string) => boolean,
): boolean => screen.view.every((slug) => hasPermission(slug));

/**
 * MAY THIS PERSON USE THIS SCREEN'S ORDINARY CREATE/EDIT/DELETE CONTROLS?
 *
 * The same conjunction rule as `canViewScreen`, over the `manage` column.
 *
 * ⚠ IT TAKES A `hasPermission`, SO IT ANSWERS ONLY WHAT IT IS ASKED — the "is there an answer
 * yet" question belongs one level up, in `pageControls.controlAnswerKnown`, which every control
 * map goes through. It used to say here that a page which MOUNTED has a known answer, on the
 * strength of `<ScreenRoute>`'s wait. That was wrong, and it was the in-page half of the fix
 * wave's blocking defect: the wait belongs to the WRAPPER's `useRBAC()` instance, and the page
 * mounts its own, which starts at `claims === null` all over again (INV-107 trap (a)).
 *
 * It answers only for the controls the screen's PRIMARY create route covers. A control with its
 * own handler asks its own constant above; that asymmetry is the whole reason this is a helper
 * over a column rather than a single `canManage` boolean per page.
 */
export const canManageScreen = (
  screen: ScreenAccessEntry,
  hasPermission: (permission: string) => boolean,
): boolean => screen.manage.every((slug) => hasPermission(slug));

/**
 * "WE DO NOT KNOW" — as opposed to "you may not", which is a different screen.
 *
 * The generalisation of `reportsAccessUnknown` (A8), which now delegates here. `permissionsUnavailable`
 * ALONE is not this question: a TRANSIENT failure keeps whatever set the store already held for
 * this uid (`mePermissions.publishPermissionsUnavailable`), so the flag can be true while every
 * answer is still usable — treating that as unknown would tell a person whose permissions we can
 * see perfectly well that we could not check them. The honest test is the flag AND an empty set:
 * every role's defaults are non-empty, so a person who holds nothing at all is a read that never
 * answered, never a real grant.
 */
export const screenAccessUnknown = (
  claims: UserClaims | null,
  permissionsUnavailable: boolean,
): boolean => permissionsUnavailable && (claims?.permissions.length ?? 0) === 0;

export type ScreenAccessVerdict = 'pending' | 'allow' | 'unknown' | 'deny';

/**
 * THE ONE DECISION, so the router, the Reports wrapper and the menu cannot disagree.
 *
 * ⚠ `pending` COMES FIRST AND IS NOT OPTIONAL (INV-107 trap (a)). Every `useRBAC()` consumer runs
 * its own `getIdTokenResult`, so a fresh one has `claims === null` — and therefore an empty
 * permission set — until THAT instance's round trip resolves, even when the shared store is warm.
 * A verdict painted before both windows close reads "you hold nothing" for everyone, Owners
 * included, on every visit; and `DEV_BYPASS` seeds the identity synchronously, so no local walk
 * and no hook-mocking test can see it.
 *
 * ⚠ THE OWNER ARM IS FOR "WE COULD NOT READ IT", NEVER FOR "IT SAID NO". An Owner holds every
 * `view:` slug by default, and the web and the API are separate Render services (INV-107 trap
 * (c)), so a bundle meeting an older backend — or an offline claims cache written against one —
 * must not bounce a clinic's Owner off their own screens. When the set ANSWERED without the slug,
 * an Owner is refused like anybody else: the server stays the authority in both directions.
 *
 * ⚠ `claims === null` AFTER `loading` CLOSES IS A THIRD STATE, AND IT IS NOT A REFUSAL. It is
 * reached whenever `useRBAC`'s token refresh rejects with nothing cached and nothing in the shared
 * identity store: the hook has STOPPED asking and still does not know who this is. Answering
 * `deny` there told a fully permitted person "You don't have access to view clients", emptied
 * every gated nav item, and — because React Router keeps a wrapper instance across sibling routes
 * — the refusal FOLLOWED them from screen to screen until a reload. A verdict needs a KNOWN
 * identity AND a KNOWN set; anything less is `pending` (still asking) or `unknown` (we could not
 * check). The OWNER arm cannot help here and is not reached: with no claims there is no role to
 * read, which is also why this check sits ABOVE it.
 */
export const screenAccessVerdict = (state: {
  loading: boolean;
  permissionsPending: boolean;
  permissionsUnavailable: boolean;
  claims: UserClaims | null;
  allowed: boolean;
}): ScreenAccessVerdict => {
  if (state.loading || state.permissionsPending) return 'pending';
  if (state.claims === null) return 'unknown';
  if (state.allowed) return 'allow';
  if (screenAccessUnknown(state.claims, state.permissionsUnavailable)) {
    return state.claims?.role === 'OWNER' ? 'allow' : 'unknown';
  }
  return 'deny';
};

/**
 * Should this nav item render? An item is hidden only once we KNOW the person cannot open it —
 * while the answer is pending, or when the read could not be made at all, the menu shows
 * everything rather than flickering empty and re-filling.
 */
export const showMenuItem = (
  requires: readonly PermissionSlug[] | readonly string[] | undefined,
  state: {
    known: boolean;
    accessUnknown: boolean;
    hasPermission: (permission: string) => boolean;
    /** The item's own `showWhilePending` (see `ScreenAccessEntry`). Omitted = true. */
    showWhilePending?: boolean;
  },
  hideWithoutAccess: boolean = HIDE_MENU_ITEMS_WITHOUT_ACCESS,
): boolean => {
  if (!requires || requires.length === 0) return true;
  if (!hideWithoutAccess) return true;
  // PENDING is where the two kinds of screen part company. Showing everything is right for the
  // ones almost everybody holds — a sidebar that empties and re-fills on every cold load reads as
  // a broken app — and wrong for the one most people do not, where a link that appears and then
  // vanishes reads as something being taken away. Reports is that one.
  if (!state.known) return state.showWhilePending ?? true;
  // A read that FAILED is a different state and keeps the old rule for both kinds: it persists
  // rather than flashing, the shell puts a banner above it, and the route explains in place.
  if (state.accessUnknown) return true;
  return requires.every((slug) => state.hasPermission(slug));
};

/**
 * The refusal sentence. Verbatim product copy — quoted by its tests, so a reword is deliberate.
 *
 * ⚠ THE CLICK PATH IS WRITTEN "Settings > Team members", NOT WITH AN ARROW (Ali, 2026-09-21).
 * Same note as the em-dash rule one release earlier, and the same reasoning: a "→" in copy a
 * clinic reads looks machine-written where a typed ">" does not. It is spaced on both sides so
 * it reads as a path and not as a comparison. `pages/Settings/permissionNotes.test.ts` guards
 * every exported sentence on this surface against both characters at once, and the backend's
 * `permissions-docs.spec.ts` holds the same spelling for the KB article and the release note.
 * Code comments — including the `findAll → view:clients` shorthand all over this file — are
 * deliberately out of scope: nobody outside this repo reads them.
 */
export const noAccessMessage = (noun: string): string =>
  `You don't have access to view ${noun}. An owner or admin can change this in Settings > Team members.`;

/** The "we could not check" sentence: blames the connection, never the person, and never redirects. */
export const accessUnknownMessage = (noun: string): string =>
  `We couldn't check your access to ${noun}. Check your connection and reload the page.`;
